import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, model, signal, untracked } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ReplikatSetupService, SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { WebSocketService } from 'src/app/services/Websocket/websocket.service';
import { ProgressEvent, ProgressGroupStatus, ProgressHandler, ProgressStatus } from 'src/app/types/progress';

@Component({
  selector: 'app-replikat-progress',
  templateUrl: './replikat-progress.component.html',
  styleUrl: './replikat-progress.component.scss',
  imports: [CommonModule, ClarityModule, MrTranslatePipe],
  providers: [WebSocketService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplikatProgressComponent {
  private readonly webSocket = inject(WebSocketService);
  private readonly replikat = inject(ReplikatSetupService);

  readonly modalOpen = model.required();
  readonly mode = input.required<'setup' | 'syncback'>();
  readonly setupConfig = input<SetupConfig>(undefined);

  protected readonly progressGroup = signal<ProgressGroupStatus>({ status: 'idle' });

  constructor() {
    effect(() => {
      const mode = this.mode();
      if (this.modalOpen())
        untracked(async () => {
          if (mode == 'syncback' && !(await this.replikat.checkIfOnline())) {
            this.modalOpen.set(false);
            return;
          }

          this.webSocket._config.name = `Replikat ${mode} Progress`;
          this.webSocket.startCon();
          this.webSocket.registerHandler('taskStarted', this.taskStarter);
          this.webSocket.registerHandler('taskProgressChanged', this.taskProgressChanger);
          this.webSocket.registerHandler('taskErrored', this.taskErrorer);
          this.webSocket.registerHandler('taskEnded', this.taskEnder);
        });
    });

    effect(() => {
      const connectionId = this.webSocket._id();
      untracked(async () => {
        if (connectionId) {
          switch (this.mode()) {
            case 'setup': {
              await this.replikat.replicate(this.setupConfig(), connectionId, this.progressHandler);
              break;
            }
            case 'syncback': {
              await this.replikat.stopReplicate('withChanges', connectionId, null, this.progressHandler);
              break;
            }
          }
          this.modalOpen.set(false);
        }
      });
    });
  }

  private taskStarter = ({ id, title }: ProgressEvent) => {
    this.progressGroup.update(group => {
      const initTask: ProgressStatus = { title, percent: 0 };
      if (group.status == 'idle' || group.status == 'done') return { status: 'pending', children: { [id]: initTask } };
      else {
        group.children[id] = initTask;
        return { ...group };
      }
    });
  };

  private taskProgressChanger = ({ id, percent, current }: ProgressEvent) => {
    this.progressGroup.update(group => {
      if (group.status != 'idle') {
        let child = group.children[id];
        child = { ...child, percent, current };
        group.children = { ...group.children, [id]: child };
      }
      return { ...group };
    });
  };

  private taskEnder = ({ id }: ProgressEvent) => {
    const group = this.progressGroup();
    if (group.status == 'idle') return;

    this.progressGroup.update(group => {
      if (group.status != 'idle') {
        delete group.children[id];
        if (!Object.keys(group.children).length) {
          return { status: 'done', children: null };
        }
      }
      return { ...group };
    });
  };

  private taskErrorer = ({ message }: ProgressEvent) => {
    console.error(message);
    this.progressGroup.update(group => ({ ...group, status: 'error', children: null }));
  };

  private readonly progressHandler: ProgressHandler = {
    taskStarter: this.taskStarter,
    taskProgressChanger: this.taskProgressChanger,
    taskErrorer: this.taskErrorer,
    taskEnder: this.taskEnder,
  };
}
